import { endpoints } from '@/services/endpoints';
import { axiosGetRequest } from '@/services/Request';
import { useQuery } from '@tanstack/react-query';
import { AppCard } from '@/components/ui/AppCard';
import { AppPieChart } from '@/components/charts/pieChart/AppPieChart';
import { AppBarChart } from '@/components/charts/pieChart/AppBarChart';
import { Skeleton } from '@/components/ui/Skeleton';

export default function UserCharts() {
  // Gender distribution
  const { data: genderData, isLoading: genderLoading } = useQuery({
    queryKey: [endpoints.getMalesAndFemalesUsersCount],
    queryFn: () => axiosGetRequest(endpoints.getMalesAndFemalesUsersCount),
    select: data => data.data,
  });

  // Age distribution
  const { data: ageData, isLoading: ageLoading } = useQuery({
    queryKey: [endpoints.getAgeChart],
    queryFn: () => axiosGetRequest(endpoints.getAgeChart),
    select: data => data.data,
  });

  // Most active users
  const { data: activeUsersData, isLoading: activeLoading } = useQuery({
    queryKey: [endpoints.getMostActiveUsers],
    queryFn: () => axiosGetRequest(endpoints.getMostActiveUsers),
  });

  const genderConfig = {
    male: { label: 'Male', color: '#3B82F6' },
    female: { label: 'Female', color: '#A855F7' },
    count: { label: 'Total', color: '#0D9373' },
  };

  const ageConfig = {
    count: { label: 'Users', color: '#0D9373' },
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-coal-900">User Analytics</h1>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Gender Distribution */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Gender Distribution</h4>
          <AppPieChart
            chartData={genderData}
            dataKey="count"
            nameKey="gender"
            config={genderConfig}
            innerData={{ value: genderData?.reduce?.((sum: number, d: any) => sum + (d.count || 0), 0) || 'N/A', title: 'Total' }}
            isLoading={genderLoading}
          />
        </AppCard>

        {/* Age Distribution */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Age Distribution</h4>
          <AppBarChart
            chartData={ageData || []}
            dataKey="count"
            nameKey="age"
            config={ageConfig}
            isLoading={ageLoading}
            className="h-80"
          />
        </AppCard>
      </div>

      {/* Most Active Users */}
      <AppCard>
        <h4 className="mb-4 text-base font-semibold text-coal-900">Most Active Users</h4>
        {activeLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded" />)}
          </div>
        ) : (activeUsersData?.data || activeUsersData?.result || []).length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-coal-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-coal-50">
                  <th className="px-4 py-2.5 text-left font-medium text-coal-600">#</th>
                  <th className="px-4 py-2.5 text-left font-medium text-coal-600">User</th>
                  <th className="px-4 py-2.5 text-left font-medium text-coal-600">Activity Count</th>
                </tr>
              </thead>
              <tbody>
                {(activeUsersData?.data || activeUsersData?.result || []).map((user: any, index: number) => (
                  <tr key={user._id || index} className="border-t border-coal-100 hover:bg-coal-50/50">
                    <td className="px-4 py-2.5 text-coal-500">{index + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-coal-800">
                      {user.fullName || user.name || user.email || 'Unknown'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                        {user.count || user.activityCount || user.totalActivities || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-coal-400">No active user data available</p>
        )}
      </AppCard>
    </div>
  );
}
